import {ReactElement, useEffect, useMemo, useState} from "react";
import {Button} from "@zendeskgarden/react-buttons";
import {Inline} from "@zendeskgarden/react-loaders";
import FileSaver from "file-saver";
import {Body, Cell, Head, HeaderCell, HeaderRow, Row, Table} from "@zendeskgarden/react-tables";
import {BiFilter} from "react-icons/bi";
import {ReactComponent as MaximizeIcon} from '@zendeskgarden/svg-icons/src/16/maximize-stroke.svg';
import {ReactComponent as MinimizeIcon} from '@zendeskgarden/svg-icons/src/16/minimize-stroke.svg';
import {
    FinancialEntityType,
    useGetSideBySideComparisonLazyQuery,
    useQueryPropertyTagsLazyQuery,
} from "../../__generated__/generated_types";
import {FinancialEntity as ChartOfAccountsEntity} from "../../contexts/chartofaccounts/ChartOfAccountsContext";
import {Property} from "../../contexts/properties/logic";
import cn from "classnames";
import {CustomZDDropdown} from "../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {AccountFilter} from "./components/account-filter/AccountFilter";
import {SettingsMenu} from "./components/settings-menu/SettingsMenu";
import {formatterDollarUSNoDecimal, formatterGlName, formatterPercentTwoDecimals, formatExportTimestampSlug} from "../../utils/formatters";
import css from "./styles/css.module.scss";

type PropertyValueType = number | "N/A" | undefined;

interface PortfolioSideBySideComparisonProps {
    snapshotId: string;
    chartOfAccountsTree: ChartOfAccountsEntity[];
    properties: Property[];
}

interface ComparisonRow {
    id: string;
    dataId: string;
    name: string;
    type: FinancialEntityType;
    depth: number;
    childIds: string[];
    parentId: string | null;
    isSubtotal: boolean;
}

interface PropertyTotals {
    budget: Record<string, number>;
    reforecast: Record<string, number>;
    monthlyUnitCount: number[];
    totalSqft: number;
}

function buildComparisonRows(entities: ChartOfAccountsEntity[], depth = 0, parentId: string | null = null): ComparisonRow[] {
    return entities.flatMap((entity) => {
        const children = buildComparisonRows(entity.children, depth + 1, entity.id);
        const allChildIds = entity.children.map(c => c.id).concat(children.flatMap(c => c.childIds));
        const name = formatterGlName(entity.name, entity.number);
        const row: ComparisonRow = {
            id: entity.id,
            dataId: entity.id,
            name: name,
            type: entity.type,
            depth: depth,
            childIds: allChildIds,
            parentId: parentId,
            isSubtotal: false
        };

        if(entity.children.length === 0) {
            return [row];
        }

        const subtotalRow: ComparisonRow = {
            id: `${entity.id}:subtotal`,
            dataId: entity.id,
            name: `Total ${name}`,
            type: entity.type,
            depth: depth,
            childIds: [],
            parentId: entity.id,
            isSubtotal: true
        };

        return [row, ...children, subtotalRow];
    });
}

export function PortfolioSideBySideComparison(props: PortfolioSideBySideComparisonProps): ReactElement {
    const {snapshotId, chartOfAccountsTree, properties} = props;

    const [fetchComparison, {data, loading, error}] = useGetSideBySideComparisonLazyQuery({
        fetchPolicy: "no-cache",
    });
    const [queryPropertyTags, {data: propertyTagsData}] = useQueryPropertyTagsLazyQuery({
        fetchPolicy: "no-cache",
    });

    const rows = useMemo(() => {
        const r = buildComparisonRows(chartOfAccountsTree);
        return new Map(r.map(row => [row.id, row]));
    }, [chartOfAccountsTree]);

    const collapsibleAccountIdSet = useMemo(() => {
        const ids = new Set<string>();
        for(const row of Array.from(rows.values())) {
            if(row.childIds.length > 0) {
                ids.add(row.id);
            }
        }
        return ids;
    }, [rows]);

    const [propertyList, setPropertyList] = useState<Property[]>(properties);
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>(() => properties.map((property) => property.id));
    const [selectedPropertyTags, setSelectedPropertyTags] = useState<string[]>([]);

    const [displayMode, setDisplayMode] = useState<"dollars" | "perUnit" | "perSqft">("dollars");

    const [expandedAccountIds, setExpandedAccountIds] = useState<Set<string>>(new Set());
    const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>(() => Array.from(rows.keys()));
    const [showAccountFilter, setShowAccountFilter] = useState(false);
    const [freezeSummaryColumns, setFreezeSummaryColumns] = useState<boolean>(false);

    const visibleRows = useMemo(() => {
        const allVisibleIds = new Set(selectedAccountIds);

        const addAllParentIds = (row: ComparisonRow, ids: Set<string>) => {
            if(row.parentId) {
                const parent = rows.get(row.parentId);
                if(parent) {
                    ids.add(row.parentId);
                    addAllParentIds(parent, ids);
                }
            }
        };

        for(const accountId of selectedAccountIds) {
            const row = rows.get(accountId);
            if(!row) {
                continue;
            }
            if(row.parentId) {
                addAllParentIds(row, allVisibleIds);
            }
        }

        const visible: ComparisonRow[] = [];
        for(const row of Array.from(rows.values())) {
            if(allVisibleIds.has(row.dataId) && (!row.parentId || expandedAccountIds.has(row.parentId))) {
                visible.push(row);
            }
        }
        return visible;
    }, [expandedAccountIds, selectedAccountIds, rows]);

    const selectedProperties = useMemo(
        () => propertyList.filter((property) => selectedPropertyIds.includes(property.id)),
        [propertyList, selectedPropertyIds]
    );

    const allPropertyTags = useMemo(
        () =>
            Array.from(
                new Set((propertyTagsData?.queryPropertyTags ?? []).flatMap((propertyTags) => propertyTags.tags))
            ),
        [propertyTagsData]
    );

    const valuesByProperty = useMemo(() => {
        const result: Record<string, PropertyTotals> = {};
        for (const propertyData of data?.getPortfolioSnapshotSideBySide.propertyData ?? []) {
            const budget: Record<string, number> = {};
            const reforecast: Record<string, number> = {};
            result[propertyData.propertyId] = {
                budget,
                reforecast,
                monthlyUnitCount: propertyData.unitCountMonthly,
                totalSqft: propertyData.totalSqft,
            };
            for (const total of [...propertyData.budgetAccountTotals, ...propertyData.budgetComponentTotals]) {
                budget[total.accountId] = total.value;
            }

            for (const total of [...propertyData.reforecastAccountTotals, ...propertyData.reforecastComponentTotals]) {
                reforecast[total.accountId] = total.value;
            }
        }
        return result;
    }, [data]);

    useEffect(() => {
        fetchComparison({
            variables: {
                snapshotId,
            },
        });
    }, [snapshotId]);

    useEffect(() => {
        setPropertyList(properties);
        setSelectedPropertyIds(properties.map((property) => property.id));
        queryPropertyTags({
            variables: {
                propertyIds: properties.map((property) => property.id),
            },
        });
    }, [properties]);

    useEffect(() => {
        if (selectedPropertyTags.length === 0 || !propertyTagsData?.queryPropertyTags.length) {
            setPropertyList(properties);
            return;
        }
        const taggedPropertyIds = new Set(
            propertyTagsData.queryPropertyTags
                .filter((propertyTags) => propertyTags.tags.some((tag) => selectedPropertyTags.includes(tag)))
                .map((propertyTags) => propertyTags.propertyId)
        );
        setPropertyList(properties.filter((property) => taggedPropertyIds.has(property.id)));
        setSelectedPropertyIds((current) => current.filter((id) => taggedPropertyIds.has(id)));
    }, [properties, selectedPropertyTags]);

    function toggleAccount(row: ComparisonRow): void {
        setExpandedAccountIds(current => {
            const newSet = new Set(current);
            if (newSet.has(row.id)) {
                newSet.delete(row.id);
                for(const childId of row.childIds) {
                    newSet.delete(childId);
                }
            } else {
                newSet.add(row.id);
            }
            return newSet;
        });
    }

    if (loading) {
        return (
            <div className={css.loading}>
                <Inline size={32} aria-label="loading" />
                Loading comparison data
            </div>
        );
    }

    if (error) {
        return <div className={css.loading}>Unable to load comparison data.</div>;
    }

    if (data?.getPortfolioSnapshotSideBySide.snapshotUpdateRequired) {
        return (
            <div className={css.loading}>
                Comparison data is not available for this snapshot. Refresh the portfolio data and try again.
            </div>
        );
    }

    const getPropertyValue = (propertyId: string, rowId: string, period: "budget" | "reforecast"): PropertyValueType => {
        const propertyTotals = valuesByProperty[propertyId];
        if (!propertyTotals) {
            return undefined;
        }

        const value = propertyTotals[period][rowId];
        if (value === undefined || displayMode === "dollars") {
            return value;
        }

        if (displayMode === "perSqft") {
            return propertyTotals.totalSqft === 0 ? "N/A" : value / propertyTotals.totalSqft;
        }

        const unitIndex = period === "reforecast" ? 11 : 23;
        const unitCount = propertyTotals.monthlyUnitCount[unitIndex];
        return unitCount !== undefined && unitCount > 0 ? value / unitCount : undefined;
    };

    const getPortfolioValue = (rowId: string, period: "budget" | "reforecast"): PropertyValueType => {
        let total = 0;
        let unitCount = 0;
        let totalSqft = 0;
        for (const property of selectedProperties) {
            const propertyTotals = valuesByProperty[property.id];
            total += propertyTotals?.[period]?.[rowId] ?? 0;
            unitCount += propertyTotals?.monthlyUnitCount[period === "reforecast" ? 11 : 23] ?? 0;
            totalSqft += propertyTotals?.totalSqft ?? 0;
        }

        if (displayMode === "perSqft") {
            return totalSqft === 0 ? "N/A" : total / totalSqft;
        }

        return displayMode === "perUnit" ? (unitCount > 0 ? total / unitCount : undefined) : total;
    };

    const NEGLIGIBLE_VARIANCE_DOLLARS = 0.01;

    const getDifference = (reforecast: PropertyValueType, budget: PropertyValueType) => {
        if(budget === "N/A" || reforecast === "N/A") {
            return "N/A";
        }
        const diff = (budget ?? 0) - (reforecast ?? 0);
        return Math.abs(diff) < NEGLIGIBLE_VARIANCE_DOLLARS ? 0 : diff;
    };

    const getDifferencePercent = (reforecast: PropertyValueType, budget: PropertyValueType) => {
        const difference = getDifference(reforecast, budget);
        return reforecast === "N/A" || difference === "N/A" || reforecast === undefined || Math.abs(reforecast) < NEGLIGIBLE_VARIANCE_DOLLARS
            ? "N/A"
            : difference / reforecast;
    };

    const formatValue = (value: PropertyValueType) => {
        return value === undefined ? formatterDollarUSNoDecimal.format(0) : value === "N/A" ? value : formatterDollarUSNoDecimal.format(value);
    };

    const formatAvgValue = (value: PropertyValueType, propertiesCount: number) => {
        if(propertiesCount === 0) {
            return "N/A";
        }
        return value === undefined ? formatterDollarUSNoDecimal.format(0) : value === "N/A" ? value : formatterDollarUSNoDecimal.format(value / propertiesCount);
    };

    const formatPercentValue = (value: PropertyValueType) => {
        return value === undefined ? formatterPercentTwoDecimals.format(0) : value === "N/A" ? value : formatterPercentTwoDecimals.format(value);
    };

    function csvValue(value: string): string {
        return `"${value.replace(/"/g, '""')}"`;
    }

    function exportAsCsv(): void {
        const headers = ["Account"];
        for (const property of selectedProperties) {
            headers.push(
                `${property.name} Reforecast`,
                `${property.name} Budget`,
                `${property.name} Var $`,
                `${property.name} Var %`
            );
        }

        if(displayMode === "dollars") {
            headers.push(
                "Portfolio Reforecast Avg",
                "Portfolio Budget Avg"
            );
        }

        headers.push(
            "Portfolio Total Reforecast",
            "Portfolio Total Budget",
            `Portfolio Total Var $`,
            "Portfolio Total Var %"
        );

        const csvRows = [headers.map(csvValue).join(",")];
        for (const row of visibleRows) {
            const isExpandedParent = !row.isSubtotal && expandedAccountIds.has(row.id) && row.childIds.length > 0;
            const values = [" ".repeat(row.depth * 4) + row.name];
            for (const property of selectedProperties) {
                if (isExpandedParent) {
                    values.push("", "", "", "");
                    continue;
                }
                const budgetVal = getPropertyValue(property.id, row.dataId, "budget");
                const reforecastVal = getPropertyValue(property.id, row.dataId, "reforecast");
                values.push(
                    formatValue(reforecastVal),
                    formatValue(budgetVal),
                    formatValue(getDifference(reforecastVal, budgetVal)),
                    formatPercentValue(getDifferencePercent(reforecastVal, budgetVal))
                );
            }

            const portfolioReforecastValue = isExpandedParent ? undefined : getPortfolioValue(row.dataId, "reforecast");
            const portfolioBudgetValue = isExpandedParent ? undefined : getPortfolioValue(row.dataId, "budget");
            if(displayMode === "dollars") {
                values.push(
                    isExpandedParent ? "" : formatAvgValue(portfolioReforecastValue, selectedProperties.length),
                    isExpandedParent ? "" : formatAvgValue(portfolioBudgetValue, selectedProperties.length)
                );
            }
            values.push(
                isExpandedParent ? "" : formatValue(portfolioReforecastValue),
                isExpandedParent ? "" : formatValue(portfolioBudgetValue),
                isExpandedParent ? "" : formatValue(getDifference(portfolioReforecastValue, portfolioBudgetValue)),
                isExpandedParent ? "" : formatPercentValue(getDifferencePercent(portfolioReforecastValue, portfolioBudgetValue))
            );
            csvRows.push(values.map(csvValue).join(","));
        }

        const saveData = new Blob([csvRows.join("\n")], {type: "text/csv;charset=utf-8"});
        FileSaver.saveAs(saveData, `Property Comparison_${formatExportTimestampSlug(new Date())}.csv`);
    }

    return (
            <div className={css.tableWrapper}>
                <div className={css.tableActions}>
                    <div className={css.actionsLeft}>
                        {
                            expandedAccountIds.size === collapsibleAccountIdSet.size ?
                                <Button onClick={() => setExpandedAccountIds(new Set())} size="small" isBasic><MinimizeIcon /></Button>
                                : <Button onClick={() => setExpandedAccountIds(collapsibleAccountIdSet)} size="small" isBasic><MaximizeIcon /></Button>
                        }
                    </div>
                    <div className={css.actionsRight}>
                        <CustomZDDropdown
                            initialSelectedItems={[displayMode]}
                            widthSpec="14rem"
                            applySelectedItems={(items) =>
                                setDisplayMode((items as string[])[0] as "dollars" | "perUnit" | "perSqft")
                            }
                            openDropdownPlaceholder="Value Format"
                            closedDropdownPlaceholder="Value Format"
                            options={[
                                {value: "dollars", label: "Total Dollars"},
                                {value: "perUnit", label: "Dollars per Unit"},
                            ]}
                            isError={undefined}
                        />
                        <div className={css.propertyFilterGroup}>
                            <CustomZDDropdown
                                initialSelectedItems={selectedPropertyTags}
                                widthSpec="12rem"
                                applySelectedItems={(items) => setSelectedPropertyTags(items as string[])}
                                openDropdownPlaceholder="Property Tags"
                                closedDropdownPlaceholder="Property Tags"
                                options={allPropertyTags.map((tag) => ({value: tag, label: tag}))}
                                isError={undefined}
                                isMulti
                                allOption
                            />
                            <CustomZDDropdown
                                initialSelectedItems={selectedPropertyIds}
                                widthSpec="12rem"
                                applySelectedItems={(items) => setSelectedPropertyIds(items as string[])}
                                openDropdownPlaceholder="Properties"
                                closedDropdownPlaceholder="Properties"
                                options={propertyList.map((property) => ({value: property.id, label: property.name}))}
                                isError={undefined}
                                isMulti
                                allOption
                            />
                        </div>
                        <Button onClick={() => setShowAccountFilter(true)}>
                            <Button.StartIcon>
                                <BiFilter />
                            </Button.StartIcon>
                            Filter Accounts
                        </Button>
                        <SettingsMenu
                            toggles={[
                                {
                                    label: "Freeze summary columns",
                                    value: freezeSummaryColumns,
                                    onToggle: () => setFreezeSummaryColumns(!freezeSummaryColumns)
                                }
                            ]}
                        />
                        <Button onClick={exportAsCsv}>Export</Button>
                    </div>
                </div>
                <div className={css.sideBySideTableOuter}>
                    <Table className={cn(css.sideBySideTable, displayMode === "dollars" && css.hasAvgColumns, freezeSummaryColumns && css.freezeSummary)}>
                        <Head isSticky>
                            <HeaderRow>
                                <HeaderCell className={css.accountHeader} rowSpan={2}>Account</HeaderCell>
                                {selectedProperties.map((property) => (
                                    <HeaderCell
                                        className={css.propertyHeader}
                                        colSpan={4}
                                        key={property.id}
                                        title={property.name}
                                    >
                                        {property.name}
                                    </HeaderCell>
                                ))}
                                <HeaderCell className={css.propertyHeader} colSpan={displayMode === "dollars" ? 6 : 4}>Portfolio Total</HeaderCell>
                            </HeaderRow>
                            <HeaderRow className={css.diffHeader}>
                                {selectedProperties.flatMap((property) => [
                                    <HeaderCell key={`${property.id}-reforecast`}>Reforecast</HeaderCell>,
                                    <HeaderCell key={`${property.id}-budget`}>Budget</HeaderCell>,
                                    <HeaderCell key={`${property.id}-difference`}>Var $</HeaderCell>,
                                    <HeaderCell key={`${property.id}-difference-percent`}>Var %</HeaderCell>,
                                ])}
                                {displayMode === "dollars" &&
                                    <>
                                        <HeaderCell className={`${css.portfolioHeader} ${displayMode === "dollars" ? css.needsBorder : ""}`}>Reforecast Avg</HeaderCell>
                                        <HeaderCell className={css.portfolioHeader}>Budget Avg</HeaderCell>
                                    </>
                                }
                                <HeaderCell className={`${css.portfolioHeader} ${displayMode !== "dollars" ? css.needsBorder : ""}`}>Reforecast</HeaderCell>
                                <HeaderCell className={css.portfolioHeader}>Budget</HeaderCell>
                                <HeaderCell className={css.portfolioHeader}>Var $</HeaderCell>
                                <HeaderCell className={css.portfolioHeader}>Var %</HeaderCell>
                            </HeaderRow>
                        </Head>
                        <Body>
                            {visibleRows.map(row => {
                                const isSubtotal = row.isSubtotal;
                                const isExpandedParent = !isSubtotal && expandedAccountIds.has(row.id) && row.childIds.length > 0;
                                const portfolioReforecastValue = isExpandedParent ? undefined : getPortfolioValue(row.dataId, "reforecast");
                                const portfolioBudgetValue = isExpandedParent ? undefined : getPortfolioValue(row.dataId, "budget");

                                return <Row key={row.id} className={isSubtotal ? css.subtotalComparisonRow : undefined}>
                                    <Cell
                                        className={`${css.accountNameCell}${isSubtotal ? ` ${css.subtotalAccountNameCell}` : ""}${
                                                collapsibleAccountIdSet.has(row.id) ? ` ${css.collapsibleAccount}` : ""
                                        }`}
                                        onClick={() => collapsibleAccountIdSet.has(row.id) && toggleAccount(row)}
                                        aria-expanded={
                                            collapsibleAccountIdSet.has(row.id)
                                                    ? expandedAccountIds.has(row.id)
                                                    : undefined
                                        }
                                        style={{paddingLeft: `${row.depth * 1.1 + 0.5}rem`}}
                                    >
                                        {collapsibleAccountIdSet.has(row.id)
                                            ? expandedAccountIds.has(row.id)
                                                    ? "- "
                                                    : "+ "
                                            : ""}
                                        {row.name}
                                    </Cell>
                                    {selectedProperties.flatMap((property) => {
                                        if (isExpandedParent) {
                                            return [
                                                <Cell key={`${property.id}-reforecast`}></Cell>,
                                                <Cell key={`${property.id}-budget`}></Cell>,
                                                <Cell key={`${property.id}-difference`}></Cell>,
                                                <Cell key={`${property.id}-difference-percent`}></Cell>,
                                            ];
                                        }
                                        const budgetVal = getPropertyValue(property.id, row.dataId, "budget");
                                        const reforecastVal = getPropertyValue(property.id, row.dataId, "reforecast");
                                        return [
                                            <Cell key={`${property.id}-reforecast`}>
                                                {formatValue(reforecastVal)}
                                            </Cell>,
                                            <Cell key={`${property.id}-budget`}>
                                                {formatValue(budgetVal)}
                                            </Cell>,
                                            <Cell key={`${property.id}-difference`}>
                                                {formatValue(getDifference(reforecastVal, budgetVal))}
                                            </Cell>,
                                            <Cell key={`${property.id}-difference-percent`}>
                                                {formatPercentValue(getDifferencePercent(reforecastVal, budgetVal))}
                                            </Cell>,
                                        ];
                                    })}
                                    {displayMode === "dollars" &&
                                        <>
                                            <Cell className={`${css.portfolioCell} ${displayMode === "dollars" ? css.needsBorder : ""}`}>{isExpandedParent ? "" : formatAvgValue(portfolioReforecastValue, selectedProperties.length)}</Cell>
                                            <Cell className={css.portfolioCell}>{isExpandedParent ? "" : formatAvgValue(portfolioBudgetValue, selectedProperties.length)}</Cell>
                                        </>
                                    }
                                    <Cell className={`${css.portfolioCell} ${displayMode !== "dollars" ? css.needsBorder : ""}`}>{isExpandedParent ? "" : formatValue(portfolioReforecastValue)}</Cell>
                                    <Cell className={css.portfolioCell}>{isExpandedParent ? "" : formatValue(portfolioBudgetValue)}</Cell>
                                    <Cell className={css.portfolioCell}>{isExpandedParent ? "" : formatValue(getDifference(portfolioReforecastValue, portfolioBudgetValue))}</Cell>
                                    <Cell className={css.portfolioCell}>{isExpandedParent ? "" : formatPercentValue(getDifferencePercent(portfolioReforecastValue, portfolioBudgetValue))}</Cell>
                                </Row>;
                            })}
                        </Body>
                    </Table>
                </div>
                {showAccountFilter && (
                    <AccountFilter
                        chartOfAccountsTree={chartOfAccountsTree}
                        onClose={() => setShowAccountFilter(false)}
                        onConfirm={(ids) => {
                            setSelectedAccountIds(ids);
                            setShowAccountFilter(false);
                        }}
                        selectedAccountIds={Array.from(selectedAccountIds)}
                        budgetYear={properties[0]?.budgetYear ?? new Date().getFullYear()}
                    />
                )}
            </div>
    );
}
